import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileBarChart, 
  Download, 
  FileSpreadsheet,
  Building2,
  Users,
  Filter,
  ChevronDown,
  ChevronRight,
  Calendar,
  PieChart,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Financial year quarters mapping
const QUARTERS = {
  Q1: { months: ['04', '05', '06'], label: 'Q1 (Apr-Jun)' },
  Q2: { months: ['07', '08', '09'], label: 'Q2 (Jul-Sep)' },
  Q3: { months: ['10', '11', '12'], label: 'Q3 (Oct-Dec)' },
  Q4: { months: ['01', '02', '03'], label: 'Q4 (Jan-Mar)' }
};

const MONTHS = [
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' }
];

const ReportsPage = () => {
  const { getAuthHeaders } = useAuth();
  const { selectedFY } = useFinancialYear();
  const [activeTab, setActiveTab] = useState('training');
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Summary Report state
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [combinedSummary, setCombinedSummary] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Modal state for drill-down
  const [drilldownModal, setDrilldownModal] = useState({
    open: false,
    title: '',
    period: '',
    source: '',
    historical: 0,
    system: 0,
    total: 0
  });
  const [summaryView, setSummaryView] = useState('quarterly'); // 'quarterly' or 'monthly'
  const [showRejected, setShowRejected] = useState(false);

  // Group sort state ('amount_desc' default, 'key_asc', 'key_desc')
  const [groupSort, setGroupSort] = useState({ training: 'amount_desc', hr: 'amount_desc' });
  
  // Training Report Filters
  const [trainingFilters, setTrainingFilters] = useState({
    dateFrom: '',
    dateTo: '',
    paymentStatus: '',
    zohoCrmId: '',
    clientName: '',
    paymentMode: '',
    groupBy: 'zoho_crm_id'
  });
  
  // HR Report Filters
  const [hrFilters, setHrFilters] = useState({
    dateFrom: '',
    dateTo: '',
    paymentStatus: '',
    category: '',
    submittedBy: '',
    paymentMode: '',
    groupBy: 'category'
  });

  const fetchExpenses = useCallback(async (expenseType) => {
    try {
      setLoading(true);
      let allExpenses = [];
      let page = 1;
      let hasMore = true;
      
      const filters = expenseType === 'CORPORATE_TRAINING' ? trainingFilters : hrFilters;
      
      // Fetch all pages
      while (hasMore) {
        const params = new URLSearchParams();
        params.append('expense_type', expenseType);
        params.append('limit', '100');
        params.append('page', page.toString());
        
        if (filters.dateFrom) params.append('start_date', filters.dateFrom);
        if (filters.dateTo) params.append('end_date', filters.dateTo);
        if (filters.paymentStatus) params.append('payment_status', filters.paymentStatus);
        
        // Exclude rejected by default unless showRejected is on
        if (!showRejected && !filters.paymentStatus) {
          params.append('payment_statuses', 'PENDING,APPROVED,PARTIALLY_PAID,PAID');
        }
        
        const response = await axios.get(`${API}/expenses?${params.toString()}`, {
          headers: getAuthHeaders()
        });
        
        const newExpenses = response.data.expenses || [];
        allExpenses = [...allExpenses, ...newExpenses];
        
        // Check if there are more pages
        if (newExpenses.length < 100 || allExpenses.length >= response.data.total) {
          hasMore = false;
        } else {
          page++;
        }
      }
      
      setExpenses(allExpenses);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch expenses:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [trainingFilters, hrFilters, showRejected, getAuthHeaders]);

  useEffect(() => {
    const expenseType = activeTab === 'training' ? 'CORPORATE_TRAINING' : 'HR_EXPENSE';
    fetchExpenses(expenseType);
  }, [activeTab, fetchExpenses]);

  const handleExport = async (format, expenseType) => {
    try {
      const params = new URLSearchParams();
      params.append('expense_type', expenseType);
      const filters = expenseType === 'CORPORATE_TRAINING' ? trainingFilters : hrFilters;
      if (filters.dateFrom) params.append('start_date', filters.dateFrom);
      if (filters.dateTo) params.append('end_date', filters.dateTo);
      if (filters.paymentStatus) params.append('payment_status', filters.paymentStatus);
      
      const response = await axios.get(`${API}/reports/export/${format}?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${expenseType.toLowerCase()}_report_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error(`Failed to export ${format}:`, error);
      toast.error(`Failed to export ${format}`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Apply client-side filters
  const applyFilters = (exps, filters, type) => {
    return exps.filter(exp => {
      const expDate = exp.reporting_date || exp.created_at?.split('T')[0];
      if (filters.dateFrom && expDate < filters.dateFrom) return false;
      if (filters.dateTo && expDate > filters.dateTo) return false;
      if (filters.paymentStatus && exp.payment_info?.payment_status !== filters.paymentStatus) return false;
      
      if (type === 'training') {
        if (filters.zohoCrmId) {
          const crmId = String(exp.corporate_project_id_new || exp.corporate_project_id || '');
          if (!crmId.toLowerCase().includes(filters.zohoCrmId.toLowerCase())) return false;
        }
        if (filters.clientName && !exp.company_name?.toLowerCase().includes(filters.clientName.toLowerCase())) return false;
      } else {
        if (filters.category && exp.category !== filters.category) return false;
        if (filters.submittedBy && !exp.submitted_by?.toLowerCase().includes(filters.submittedBy.toLowerCase())) return false;
      }
      
      if (filters.paymentMode) {
        const payments = exp.payments || [];
        const hasMode = payments.some(p => p.mode_of_payment === filters.paymentMode);
        const infoMode = exp.payment_info?.mode_of_payment === filters.paymentMode;
        if (!hasMode && !infoMode) return false;
      }
      
      return true;
    });
  };

  // Group expenses based on groupBy field
  const groupExpenses = (exps, groupBy, type) => {
    const groups = {};
    
    exps.forEach(exp => {
      let groupKey;
      if (type === 'training') {
        switch (groupBy) {
          case 'zoho_crm_id':
            groupKey = String(exp.corporate_project_id_new || exp.corporate_project_id || 'No ID');
            break;
          case 'client_name':
            groupKey = exp.company_name || 'Unknown';
            break;
          case 'month':
            const date = exp.reporting_date || exp.created_at?.split('T')[0];
            groupKey = date ? date.substring(0, 7) : 'Unknown';
            break;
          case 'payment_status':
            groupKey = exp.payment_info?.payment_status || 'PENDING';
            break;
          default:
            groupKey = 'All';
        }
      } else {
        switch (groupBy) {
          case 'category':
            groupKey = exp.category || 'Uncategorized';
            break;
          case 'submitted_by':
            groupKey = exp.submitted_by || 'Unknown';
            break;
          case 'month':
            const date = exp.reporting_date || exp.created_at?.split('T')[0];
            groupKey = date ? date.substring(0, 7) : 'Unknown';
            break;
          case 'payment_status':
            groupKey = exp.payment_info?.payment_status || 'PENDING';
            break;
          default:
            groupKey = 'All';
        }
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          expenses: [],
          totalAmount: 0,
          paidAmount: 0,
          pendingAmount: 0,
          count: 0
        };
      }
      
      const expAmount = exp.payment_info?.amount_to_pay_inr || exp.total_expenses || 0;
      const payments = exp.payments || [];
      const paidSoFar = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
      
      groups[groupKey].expenses.push(exp);
      groups[groupKey].totalAmount += expAmount;
      groups[groupKey].paidAmount += paidSoFar;
      groups[groupKey].pendingAmount += Math.max(0, expAmount - paidSoFar);
      groups[groupKey].count += 1;
    });
    
    return Object.values(groups).sort((a, b) => b.totalAmount - a.totalAmount);
  };

  // Calculate totals
  const calculateTotals = (groups) => {
    return groups.reduce((acc, g) => ({
      totalAmount: acc.totalAmount + g.totalAmount,
      paidAmount: acc.paidAmount + g.paidAmount,
      pendingAmount: acc.pendingAmount + g.pendingAmount,
      count: acc.count + g.count
    }), { totalAmount: 0, paidAmount: 0, pendingAmount: 0, count: 0 });
  };

  // Fetch combined summary (historical + system)
  const fetchCombinedSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const response = await axios.get(`${API}/historical/summary/${selectedFY}`, {
        headers: getAuthHeaders()
      });
      setCombinedSummary(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch summary:', error);
      toast.error('Failed to load summary data');
    } finally {
      setSummaryLoading(false);
    }
  }, [selectedFY, getAuthHeaders]);

  useEffect(() => {
    if (activeTab === 'summary') {
      fetchCombinedSummary();
    }
  }, [activeTab, selectedFY, fetchCombinedSummary]);

  // Toggle drill-down row
  const toggleRowExpand = (key) => {
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Open drill-down modal
  const openDrilldownModal = (title, period, source, data) => {
    setDrilldownModal({
      open: true,
      title,
      period,
      source,
      historical: data.historical || 0,
      system: data.system || 0,
      total: data.total || 0
    });
  };

  // Close drill-down modal
  const closeDrilldownModal = () => {
    setDrilldownModal(prev => ({ ...prev, open: false }));
  };

  // Render combined summary table with drill-down
  const renderCombinedSummaryTable = (title, dataKey, icon, bgColor) => {
    if (!combinedSummary) return null;
    
    const data = summaryView === 'quarterly' ? combinedSummary.quarterly : combinedSummary.monthly;
    const ytd = combinedSummary.ytd[dataKey];
    
    // Get display title for modal
    const getDisplayTitle = () => {
      if (dataKey === 'training') return 'Training Expenses';
      if (dataKey === 'hr_salary') return 'HR Expenses (Salary)';
      if (dataKey === 'hr_other') return 'HR Expenses (Other)';
      return title;
    };
    
    return (
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
            {icon}
            {title} - FY {selectedFY}
          </CardTitle>
          <CardDescription>
            {summaryView === 'quarterly' ? 'Quarterly' : 'Monthly'} breakdown • Click row to see Historical vs System split
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 font-semibold text-slate-600 w-8"></th>
                  <th className="text-left p-3 font-semibold text-slate-600">
                    {summaryView === 'quarterly' ? 'Quarter' : 'Month'}
                  </th>
                  <th className="text-center p-3 font-semibold text-slate-600">Source</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, idx) => {
                  const rowKey = `${dataKey}-${item.quarter || item.month}`;
                  const itemData = item[dataKey];
                  const hasBreakdown = itemData.historical > 0 || itemData.system > 0;
                  
                  return (
                    <tr 
                      key={rowKey}
                      className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} ${hasBreakdown ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                      onClick={() => hasBreakdown && openDrilldownModal(
                        getDisplayTitle(),
                        item.label,
                        item.source,
                        itemData
                      )}
                      data-testid={`summary-row-${rowKey}`}
                    >
                      <td className="p-3 text-slate-400">
                        {hasBreakdown && (
                          <Info className="w-4 h-4" />
                        )}
                      </td>
                      <td className="p-3 font-medium text-slate-900">{item.label}</td>
                      <td className="p-3 text-center">
                        {item.source === 'historical' && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Historical</Badge>
                        )}
                        {item.source === 'system' && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">System</Badge>
                        )}
                        {item.source === 'mixed' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Mixed</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono font-semibold text-slate-900">
                        {formatCurrency(itemData.total)}
                      </td>
                    </tr>
                  );
                })}
                <tr className={`${bgColor} font-semibold`}>
                  <td className="p-3"></td>
                  <td className="p-3">Total (YTD)</td>
                  <td className="p-3"></td>
                  <td className="p-3 text-right font-mono">{formatCurrency(ytd.total)}</td>
                </tr>
                {/* YTD Breakdown */}
                <tr className="bg-slate-50 text-sm">
                  <td></td>
                  <td colSpan="3" className="p-2">
                    <div className="flex gap-6">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span className="text-slate-600">Historical Total:</span>
                        <span className="font-mono font-medium">{formatCurrency(ytd.historical)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        <span className="text-slate-600">System Total:</span>
                        <span className="font-mono font-medium">{formatCurrency(ytd.system)}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Get unique categories for HR filter
  const getUniqueCategories = () => {
    const categories = new Set();
    expenses.forEach(exp => {
      if (exp.category) categories.add(exp.category);
    });
    return Array.from(categories);
  };

  const renderFilters = (type) => {
    const filters = type === 'training' ? trainingFilters : hrFilters;
    const setFilters = type === 'training' ? setTrainingFilters : setHrFilters;
    
    return (
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Start Date</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                className="h-9 w-36"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">End Date</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                className="h-9 w-36"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Payment Status</Label>
              <Select
                value={filters.paymentStatus || "all"}
                onValueChange={(val) => setFilters({...filters, paymentStatus: val === "all" ? "" : val})}
              >
                <SelectTrigger className="h-9 w-32">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="PARTIALLY_PAID">Partial</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {type === 'training' ? (
              <>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Zoho CRM ID</Label>
                  <Input
                    placeholder="Search..."
                    value={filters.zohoCrmId}
                    onChange={(e) => setFilters({...filters, zohoCrmId: e.target.value})}
                    className="h-9 w-28"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Client Name</Label>
                  <Input
                    placeholder="Search..."
                    value={filters.clientName}
                    onChange={(e) => setFilters({...filters, clientName: e.target.value})}
                    className="h-9 w-32"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Category</Label>
                  <Select
                    value={filters.category || "all"}
                    onValueChange={(val) => setFilters({...filters, category: val === "all" ? "" : val})}
                  >
                    <SelectTrigger className="h-9 w-32">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {getUniqueCategories().map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Submitted By</Label>
                  <Input
                    placeholder="Search..."
                    value={filters.submittedBy}
                    onChange={(e) => setFilters({...filters, submittedBy: e.target.value})}
                    className="h-9 w-32"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Payment Mode</Label>
              <Select
                value={filters.paymentMode || "all"}
                onValueChange={(val) => setFilters({...filters, paymentMode: val === "all" ? "" : val})}
              >
                <SelectTrigger className="h-9 w-28">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Omnicard">Omnicard</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Deposit">Deposit</SelectItem>
                  <SelectItem value="Online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Group By</Label>
              <Select
                value={filters.groupBy}
                onValueChange={(val) => setFilters({...filters, groupBy: val})}
              >
                <SelectTrigger className="h-9 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {type === 'training' ? (
                    <>
                      <SelectItem value="zoho_crm_id">Zoho CRM ID</SelectItem>
                      <SelectItem value="client_name">Client Name</SelectItem>
                      <SelectItem value="month">Month</SelectItem>
                      <SelectItem value="payment_status">Payment Status</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="submitted_by">Submitted By</SelectItem>
                      <SelectItem value="month">Month</SelectItem>
                      <SelectItem value="payment_status">Payment Status</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const defaultFilters = type === 'training' 
                  ? { dateFrom: '', dateTo: '', paymentStatus: '', zohoCrmId: '', clientName: '', paymentMode: '', groupBy: 'zoho_crm_id' }
                  : { dateFrom: '', dateTo: '', paymentStatus: '', category: '', submittedBy: '', paymentMode: '', groupBy: 'category' };
                setFilters(defaultFilters);
              }}
              className="h-9"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderReport = (type) => {
    const filters = type === 'training' ? trainingFilters : hrFilters;
    const filteredExps = applyFilters(expenses, filters, type);
    const grouped = groupExpenses(filteredExps, filters.groupBy, type);
    const sortMode = groupSort[type];
    const groups = [...grouped].sort((a, b) => {
      if (sortMode === 'key_asc') return String(a.key).localeCompare(String(b.key));
      if (sortMode === 'key_desc') return String(b.key).localeCompare(String(a.key));
      return b.totalAmount - a.totalAmount;
    });
    const totals = calculateTotals(groups);
    const expenseType = type === 'training' ? 'CORPORATE_TRAINING' : 'HR_EXPENSE';
    const cycleSort = () => {
      setGroupSort(prev => {
        const next = prev[type] === 'amount_desc' ? 'key_asc' : prev[type] === 'key_asc' ? 'key_desc' : 'amount_desc';
        return { ...prev, [type]: next };
      });
    };
    const sortIcon = sortMode === 'key_asc' ? '▲' : sortMode === 'key_desc' ? '▼' : '↕';

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Expenses</p>
              <p className="text-2xl font-bold font-mono text-indigo-950">{totals.count}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Amount</p>
              <p className="text-2xl font-bold font-mono text-indigo-950">{formatCurrency(totals.totalAmount)}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <p className="text-xs text-emerald-600">Paid Amount</p>
              <p className="text-2xl font-bold font-mono text-emerald-700">{formatCurrency(totals.paidAmount)}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <p className="text-xs text-amber-600">Pending Amount</p>
              <p className="text-2xl font-bold font-mono text-amber-700">{formatCurrency(totals.pendingAmount)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Export Buttons */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleExport('excel', expenseType)}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleExport('pdf', expenseType)}
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>

        {/* Grouped Report Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading text-indigo-950">
              {type === 'training' ? 'Training Expense Report' : 'HR Expense Report'}
            </CardTitle>
            <CardDescription>
              Grouped by {filters.groupBy.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • Click to expand
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="h-12 bg-slate-100 rounded animate-pulse"></div>
                ))}
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No expenses found matching the filters
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="w-8 p-3"></th>
                      <th
                        className="text-left p-3 font-semibold text-slate-600 cursor-pointer select-none hover:bg-slate-100"
                        onClick={cycleSort}
                        data-testid={`report-sort-key-${type}`}
                        title="Click to toggle sort: amount desc → A→Z → Z→A"
                      >
                        {filters.groupBy.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        <span className="ml-2 text-xs text-indigo-600">{sortIcon}</span>
                      </th>
                      <th className="text-right p-3 font-semibold text-slate-600">Total Amount</th>
                      <th className="text-right p-3 font-semibold text-slate-600">Paid</th>
                      <th className="text-right p-3 font-semibold text-slate-600">Pending</th>
                      <th className="text-center p-3 font-semibold text-slate-600">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group, idx) => (
                      <React.Fragment key={group.key || `group-${idx}`}>
                        <tr 
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleGroup(group.key)}
                        >
                          <td className="p-3 text-center">
                            {expandedGroups[group.key] ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </td>
                          <td className="p-3 font-medium">{group.key}</td>
                          <td className="p-3 text-right font-mono font-semibold">{formatCurrency(group.totalAmount)}</td>
                          <td className="p-3 text-right font-mono text-emerald-600">{formatCurrency(group.paidAmount)}</td>
                          <td className="p-3 text-right font-mono text-amber-600">{formatCurrency(group.pendingAmount)}</td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className="font-mono">{group.count}</Badge>
                          </td>
                        </tr>
                        {expandedGroups[group.key] && group.expenses.map((exp, expIdx) => {
                          const expAmount = exp.payment_info?.amount_to_pay_inr || exp.total_expenses || 0;
                          const payments = exp.payments || [];
                          const paidSoFar = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
                          return (
                            <tr key={`${idx}-${expIdx}`} className="bg-slate-50 border-b border-slate-100">
                              <td className="p-2"></td>
                              <td className="p-2 pl-6 text-sm">
                                <span className="text-slate-400 mr-2">└</span>
                                {type === 'training' ? exp.training_name || exp.company_name : exp.description || exp.category}
                                <Badge 
                                  className={`ml-2 font-mono text-xs ${
                                    exp.payment_info?.payment_status === 'PAID' ? 'bg-emerald-100 text-emerald-700' :
                                    exp.payment_info?.payment_status === 'PARTIALLY_PAID' ? 'bg-blue-100 text-blue-700' :
                                    exp.payment_info?.payment_status === 'APPROVED' ? 'bg-indigo-100 text-indigo-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}
                                >
                                  {exp.payment_info?.payment_status || 'PENDING'}
                                </Badge>
                              </td>
                              <td className="p-2 text-right font-mono text-sm">{formatCurrency(expAmount)}</td>
                              <td className="p-2 text-right font-mono text-sm text-emerald-600">{formatCurrency(paidSoFar)}</td>
                              <td className="p-2 text-right font-mono text-sm text-amber-600">{formatCurrency(Math.max(0, expAmount - paidSoFar))}</td>
                              <td className="p-2 text-center">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 text-xs text-indigo-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `/expenses/${exp.id}`;
                                  }}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100">
                    <tr>
                      <td className="p-3"></td>
                      <td className="p-3 font-semibold">Total</td>
                      <td className="p-3 text-right font-mono font-bold">{formatCurrency(totals.totalAmount)}</td>
                      <td className="p-3 text-right font-mono font-bold text-emerald-600">{formatCurrency(totals.paidAmount)}</td>
                      <td className="p-3 text-right font-mono font-bold text-amber-600">{formatCurrency(totals.pendingAmount)}</td>
                      <td className="p-3 text-center font-mono font-bold">{totals.count}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950">Reports</h1>
          <p className="text-slate-500 text-sm mt-1">Generate and export expense reports</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div></div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={showRejected} onChange={(e) => setShowRejected(e.target.checked)} className="rounded border-slate-300" data-testid="report-show-rejected-toggle" />
          <span className="text-slate-600">Include Rejected Expenses</span>
        </label>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="training" className="data-[state=active]:bg-white" data-testid="training-report-tab">
            <Building2 className="w-4 h-4 mr-2" />
            Training Report
          </TabsTrigger>
          <TabsTrigger value="hr" className="data-[state=active]:bg-white" data-testid="hr-report-tab">
            <Users className="w-4 h-4 mr-2" />
            HR Report
          </TabsTrigger>
          <TabsTrigger value="summary" className="data-[state=active]:bg-white" data-testid="summary-report-tab">
            <PieChart className="w-4 h-4 mr-2" />
            Summary Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="training" className="mt-0 space-y-4">
          {renderFilters('training')}
          {renderReport('training')}
        </TabsContent>

        <TabsContent value="hr" className="mt-0 space-y-4">
          {renderFilters('hr')}
          {renderReport('hr')}
        </TabsContent>

        <TabsContent value="summary" className="mt-0 space-y-6">
          {/* Summary Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Showing data for</span>
                  <span className="font-semibold text-indigo-700">FY {selectedFY}</span>
                  <span className="text-slate-400">(Change from sidebar)</span>
                </div>
                <div className="ml-auto space-y-1">
                  <Label className="text-xs text-slate-500">View By</Label>
                  <Select value={summaryView} onValueChange={setSummaryView}>
                    <SelectTrigger className="h-9 w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Reports */}
          {summaryLoading ? (
            <div className="space-y-4">
              {[1, 2].map(i => (
                <Card key={i} className="border-slate-200">
                  <CardContent className="p-6">
                    <div className="h-40 bg-slate-100 rounded animate-pulse"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {renderCombinedSummaryTable('Training Expenses', 'training', <Building2 className="w-5 h-5 text-indigo-600" />, 'bg-indigo-50 text-indigo-900')}
              {renderCombinedSummaryTable('HR Expenses (Salary)', 'hr_salary', <Users className="w-5 h-5 text-blue-600" />, 'bg-blue-50 text-blue-900')}
              {renderCombinedSummaryTable('HR Expenses (Other)', 'hr_other', <Users className="w-5 h-5 text-purple-600" />, 'bg-purple-50 text-purple-900')}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Drill-down Modal */}
      <Dialog open={drilldownModal.open} onOpenChange={(open) => !open && closeDrilldownModal()}>
        <DialogContent className="sm:max-w-[480px]" data-testid="drilldown-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-950">
              <PieChart className="w-5 h-5 text-indigo-600" />
              {drilldownModal.title}
            </DialogTitle>
            <DialogDescription>
              Breakdown for {drilldownModal.period}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Source Badge */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Data Source:</span>
              {drilldownModal.source === 'historical' && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Historical Only</Badge>
              )}
              {drilldownModal.source === 'system' && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">System Only</Badge>
              )}
              {drilldownModal.source === 'mixed' && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Mixed (Historical + System)</Badge>
              )}
            </div>
            
            {/* Breakdown Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    <span className="text-sm font-medium text-blue-700">Historical</span>
                  </div>
                  <p className="text-2xl font-bold font-mono text-blue-900" data-testid="modal-historical-amount">
                    {formatCurrency(drilldownModal.historical)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">Manually entered data</p>
                </CardContent>
              </Card>
              
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    <span className="text-sm font-medium text-green-700">System</span>
                  </div>
                  <p className="text-2xl font-bold font-mono text-green-900" data-testid="modal-system-amount">
                    {formatCurrency(drilldownModal.system)}
                  </p>
                  <p className="text-xs text-green-600 mt-1">System calculated</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Total */}
            <Card className="border-indigo-200 bg-indigo-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-indigo-700">Combined Total</p>
                    <p className="text-xs text-indigo-500 mt-0.5">Historical + System</p>
                  </div>
                  <p className="text-3xl font-bold font-mono text-indigo-900" data-testid="modal-total-amount">
                    {formatCurrency(drilldownModal.total)}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Percentage Breakdown */}
            {drilldownModal.total > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 font-medium">Contribution</p>
                <div className="h-3 bg-slate-200 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-blue-500 transition-all duration-300"
                    style={{ width: `${(drilldownModal.historical / drilldownModal.total) * 100}%` }}
                  />
                  <div 
                    className="bg-green-500 transition-all duration-300"
                    style={{ width: `${(drilldownModal.system / drilldownModal.total) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Historical: {((drilldownModal.historical / drilldownModal.total) * 100).toFixed(1)}%</span>
                  <span>System: {((drilldownModal.system / drilldownModal.total) * 100).toFixed(1)}%</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
